import React from 'react';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import ErrorBoundary from '@atlassian/eoc-error-boundary';
import { eocUserResource } from '@atlassian/eoc-resources';
import { ResponderNamesWrapper } from '@atlassian/eoc-responder-names';
import { useResource } from '@atlassian/eoc-rrr-helpers';
import { getTimezonesResource } from '@atlassian/eoc-schedule-common/resources';

import { ANALYTICS_TAGS } from '../common/constants';
import { Container as PageStoreContainer } from '../controllers/page-store';
import { Container as ReadyStoreContainer } from '../controllers/ready-store';
import {
	getScheduleRightsResource,
	getSchedulesResource,
	getScheduleTimeTemplatesResource,
	getUiPreferencesResource,
} from '../services/resources';

import { List } from './list';
import { Section } from './section';
import { type SchedulePageProps } from './types';

const SchedulePage = ({ onSchedulesReady }: SchedulePageProps) => {
	const { loading: getScheduleRightsLoading, error: getScheduleRightsError } =
		useResource(getScheduleRightsResource);
	const { loading: getSchedulesLoading, error: getSchedulesError } =
		useResource(getSchedulesResource);
	const { loading: getUiPreferencesLoading, error: getUiPreferencesError } =
		useResource(getUiPreferencesResource);
	const { loading: getScheduleTimeTemplatesLoading, error: getScheduleTimeTemplatesError } =
		useResource(getScheduleTimeTemplatesResource);
	const { loading: eocUserLoading, error: eocUserError } = useResource(eocUserResource);
	const { loading: getTimezonesLoading, error: getTimezonesError } =
		useResource(getTimezonesResource);

	const isError =
		getScheduleRightsError ||
		getSchedulesError ||
		getUiPreferencesError ||
		getScheduleTimeTemplatesError ||
		eocUserError ||
		getTimezonesError;
	const isLoading =
		getScheduleRightsLoading ||
		getSchedulesLoading ||
		getUiPreferencesLoading ||
		getScheduleTimeTemplatesLoading ||
		eocUserLoading ||
		getTimezonesLoading;

	if (isError || isLoading) {
		return null;
	}

	return (
		<AnalyticsContext
			data={{
				source: 'onCall',
				tags: ANALYTICS_TAGS.onCall,
			}}
		>
			<ErrorBoundary>
				<ResponderNamesWrapper>
					<ReadyStoreContainer onAllSchedulesReady={onSchedulesReady}>
						<PageStoreContainer>
							<Section>
								<List />
							</Section>
						</PageStoreContainer>
					</ReadyStoreContainer>
				</ResponderNamesWrapper>
			</ErrorBoundary>
		</AnalyticsContext>
	);
};

export { SchedulePage };
